import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import Logo from './Logo';
import Footer from './Footer';
import ThemeToggle from './ThemeToggle';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from './ui/dropdown-menu';
import { 
    Menu, 
    X, 
    User, 
    LogOut, 
    Settings,
    FileText,
    LayoutDashboard,
    Search,
    HardHat,
    Plus
} from 'lucide-react';

const Layout = ({ children }) => {
    const { user, logout, isAdmin, isModerator } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navItems = [
        { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { href: '/search', label: 'Search', icon: Search },
        { href: '/profile', label: 'Profile', icon: User }
    ];
    
    // Mobile nav items (with quick add in center)
    const mobileNavItems = [
        { href: '/dashboard', label: 'Home', icon: LayoutDashboard },
        { href: '/search', label: 'Search', icon: Search },
        { href: '/dashboard', label: 'Add', icon: Plus, isAction: true, scrollTo: 'quick-add' },
        { href: '/profile', label: 'Profile', icon: User }
    ];

    if (isModerator) {
        navItems.push({ href: '/moderation', label: 'Moderation', icon: FileText });
    }

    if (isAdmin) {
        navItems.push({ href: '/admin', label: 'Admin', icon: Settings });
    }

    return (
        <div className="min-h-screen flex flex-col bg-background theme-transition">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-border header-themed theme-transition">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        {/* Logo */}
                        <Link to={user ? '/dashboard' : '/'} className="flex items-center">
                            <Logo variant="full" size="small" />
                        </Link>

                        {/* Desktop Navigation */}
                        {user && (
                            <nav className="hidden md:flex items-center gap-1">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                                            location.pathname === item.href
                                                ? 'bg-primary text-primary-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                                        }`}
                                    >
                                        <item.icon className="h-4 w-4" />
                                        {item.label}
                                    </Link>
                                ))}
                            </nav>
                        )}

                        {/* User Menu */}
                        <div className="flex items-center gap-4">
                            {user ? (
                                <>
                                    {/* Theme Toggle */}
                                    <ThemeToggle className="hidden md:flex" />

                                    {/* Desktop User Menu */}
                                    <div className="hidden md:block">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button 
                                                    variant="ghost" 
                                                    className="flex items-center gap-2"
                                                    data-testid="user-menu-trigger"
                                                >
                                                    <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                                                        <HardHat className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {user.company_name}
                                                    </span>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-56">
                                                <div className="px-2 py-1.5">
                                                    <p className="text-sm font-medium">{user.company_name}</p>
                                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                                </div>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => navigate('/profile')}>
                                                    <User className="mr-2 h-4 w-4" />
                                                    Profile
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => navigate('/settings')}>
                                                    <Settings className="mr-2 h-4 w-4" />
                                                    Account Settings
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                                                    <LogOut className="mr-2 h-4 w-4" />
                                                    Logout
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>

                                    {/* Mobile: Theme + Menu */}
                                    <div className="flex items-center gap-1 md:hidden">
                                        <ThemeToggle />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                            data-testid="mobile-menu-btn"
                                        >
                                            {mobileMenuOpen ? (
                                                <X className="h-6 w-6" />
                                            ) : (
                                                <Menu className="h-6 w-6" />
                                            )}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <ThemeToggle />
                                    <Link to="/login">
                                        <Button variant="ghost" data-testid="nav-login-btn">
                                            Log In
                                        </Button>
                                    </Link>
                                    <Link to="/signup">
                                        <Button data-testid="nav-signup-btn">
                                            Get Started
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {user && mobileMenuOpen && (
                    <div className="md:hidden border-t border-border bg-card">
                        <div className="px-4 py-4 space-y-2">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                                        location.pathname === item.href
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                                    }`}
                                >
                                    <item.icon className="h-5 w-5" />
                                    {item.label}
                                </Link>
                            ))}
                            <div className="border-t border-border pt-2 mt-2">
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center gap-3 px-4 py-3 w-full text-left text-muted-foreground hover:text-foreground hover:bg-accent rounded-md"
                                    data-testid="mobile-logout-btn"
                                >
                                    <LogOut className="h-5 w-5" />
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Main Content - add padding for mobile bottom nav */}
            <main className="flex-1 pb-20 md:pb-0">{children}</main>

            {/* Mobile Bottom Navigation - sticky, thumb-friendly */}
            {user && (
                <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border safe-area-bottom">
                    <div className="flex justify-around items-center h-16">
                        {mobileNavItems.map((item, index) => {
                            // Special handling for center "Add" button
                            if (item.isAction) {
                                return (
                                    <button
                                        key={item.label}
                                        onClick={() => {
                                            navigate('/dashboard');
                                            // Scroll to quick add section after navigation
                                            setTimeout(() => {
                                                const el = document.getElementById('quick-add-section');
                                                if (el) el.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        }}
                                        className="flex flex-col items-center justify-center flex-1 h-full min-w-[64px] py-2"
                                        data-testid="mobile-nav-add"
                                    >
                                        <div className="bg-primary text-primary-foreground rounded-full p-2 -mt-4 shadow-lg">
                                            <item.icon className="h-5 w-5" />
                                        </div>
                                        <span className="text-xs font-medium text-primary mt-1">{item.label}</span>
                                    </button>
                                );
                            }
                            
                            return (
                                <Link
                                    key={item.href + item.label}
                                    to={item.href}
                                    className={`flex flex-col items-center justify-center flex-1 h-full min-w-[64px] py-2 transition-colors ${
                                        location.pathname === item.href && !item.isAction
                                            ? 'text-primary'
                                            : 'text-muted-foreground'
                                    }`}
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                >
                                    <item.icon className="h-5 w-5 mb-1" />
                                    <span className="text-xs font-medium">{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </nav>
            )}

            {/* Footer - hide on mobile when logged in (bottom nav takes priority) */}
            <div className={user ? 'hidden md:block' : ''}>
                <Footer />
            </div>
        </div>
    );
};

export default Layout;
